import { getSize } from '@/utils/iterable/getSize';

import { ERRORS, IMAGE_FILE_FORMATS, MAX_ALT_TEXT_LENGTH, MAX_PHOTO_SIZE, PHOTO_DIMENSIONS_MAXIMUM, PHOTO_DIMENSIONS_MINIMUM, PHOTO_DIMENSIONS_RECOMMENDED } from '@/constants/validations';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { EMPTY, SEPARATOR } from '@/constants';
import { ERROR } from '@/constants/product';

export async function getUploadedPhotoError({ channel, file, url }) {
  return new Promise(function executor(resolve, reject) {
    const imageType = (file?.type || '').split(SEPARATOR.SLASH)[1];

    if (
      !imageType || (
        IMAGE_FILE_FORMATS.hasOwnProperty(channel) &&
        !IMAGE_FILE_FORMATS[channel].has(imageType.toLowerCase())
      )
    ) {
      reject(ERRORS.PHOTOS.UNSUPPORTED_FORMAT[channel]);
    }

    if (MAX_PHOTO_SIZE.hasOwnProperty(channel) && file.size > MAX_PHOTO_SIZE[channel]) {
      reject(ERRORS.PHOTOS.FILE_TOO_BIG[channel]);
    }

    const image = document.createElement('img');

    image.onerror = function imageError() {
      reject(ERRORS.PHOTOS.UNKNOWN);
    };

    image.onload = function imageLoaded() {
      const { width, height } = image;
      const minimum = PHOTO_DIMENSIONS_MINIMUM[channel];
      const maximum = PHOTO_DIMENSIONS_MAXIMUM[channel];

      if (Number.isFinite(minimum)) {
        if (width < minimum || height < minimum) {
          reject(ERRORS.PHOTOS.TOO_SMALL[channel]);
          return;
        }
      }

      if (Number.isFinite(maximum)) {
        if (width > maximum || height > maximum) {
          reject(ERRORS.PHOTOS.TOO_BIG[channel]);
          return;
        }
      }

      resolve(file);
    };

    image.src = url || URL.createObjectURL(file);
  });
}

export function getPhotosError({ channel, photos }) {
  switch (channel) {
    case EBAY:
    case ETSY:
    case FAIRE: {
      if (getSize(photos) === 0) return ERRORS.PHOTOS.REQUIRED;

      break;
    }

    default: {
      if (getSize(photos) === 0) return false;

      break;
    }
  }

  function reducePhotos(error, photo) {
    return error || photo.get(ERROR, false);
  }

  return photos.reduce(reducePhotos, false);
}

export function getAltTextError({ channel, value }) {
  return (
    channel in MAX_ALT_TEXT_LENGTH &&
    getSize(value) > MAX_ALT_TEXT_LENGTH[channel] &&
    ERRORS.PHOTOS.ALT_TEXT_TOO_LONG[channel]
  );
}

export function getPhotoAttributeError({ attribute, channel, value }) {
  switch (attribute) {
    case 'alt_text': {
      return getAltTextError({ channel, value });
    }

    default: {
      return false;
    }
  }
}

export function getPhotosOptimizationErrors({ channel, photos }) {
  function reducePhotos(result, photo, index) {
    let errors = EMPTY.MAP;

    function getResolutionError() {
      return (
        channel in PHOTO_DIMENSIONS_RECOMMENDED && (
          photo.get('width') < PHOTO_DIMENSIONS_RECOMMENDED[channel] ||
          photo.get('height') < PHOTO_DIMENSIONS_RECOMMENDED[channel]
        )
      );
    }

    function getNoAltTextError() {
      return !photo.get('alt_text');
    }

    if (getResolutionError()) {
      errors = errors.set('resolution', ERRORS.PHOTOS.LOW_RESOLUTION);
    }

    if (channel === ETSY || channel === SHOPIFY) {
      if (getNoAltTextError()) {
        errors = errors.set('altText', ERRORS.PHOTOS.ALT_TEXT_MISSING);
      }
    }

    return getSize(errors) > 0
      ? result.set(index, errors)
      : result;
  }

  return photos.reduce(reducePhotos, EMPTY.MAP);
}
